const HEX6 = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX6.test(value);
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function luminance(r: number, g: number, b: number): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function rgb(hex: string): { r: number; g: number; b: number } | null {
  if (!isHexColor(hex)) return null;
  return {
    r: Number.parseInt(hex.slice(1, 3), 16),
    g: Number.parseInt(hex.slice(3, 5), 16),
    b: Number.parseInt(hex.slice(5, 7), 16),
  };
}

function hex(r: number, g: number, b: number): string {
  const part = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

function contrastRatio(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }): number {
  const l1 = luminance(a.r, a.g, a.b);
  const l2 = luminance(b.r, b.g, b.b);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/** Text color that stays readable on a solid hex fill. */
export function contrastInk(hexColor: string): "#0B1622" | "#FFFFFF" {
  const parsed = rgb(hexColor);
  if (!parsed) return "#FFFFFF";
  const yiq = (parsed.r * 299 + parsed.g * 587 + parsed.b * 114) / 1000;
  return yiq >= 150 ? "#0B1622" : "#FFFFFF";
}

/** Secondary text on a solid hex fill. */
export function contrastMuted(hexColor: string): string {
  return contrastInk(hexColor) === "#0B1622" ? "rgba(11,22,34,0.7)" : "rgba(255,255,255,0.7)";
}

/**
 * A stripe or progress color on the dark app surface. Dark custom hexes are
 * mixed toward white until they clear the surface; light hexes stay put.
 */
export function tintOnDark(hexColor: string, surface = "#0B1622"): string {
  const parsed = rgb(hexColor);
  const bg = rgb(surface);
  if (!parsed || !bg) return "#7b97c0";
  let cur = parsed;
  for (let i = 0; i < 8; i++) {
    if (contrastRatio(cur, bg) >= 3) return hex(cur.r, cur.g, cur.b);
    cur = {
      r: Math.round(cur.r + (255 - cur.r) * 0.4),
      g: Math.round(cur.g + (255 - cur.g) * 0.4),
      b: Math.round(cur.b + (255 - cur.b) * 0.4),
    };
  }
  return hex(cur.r, cur.g, cur.b);
}
